import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig, CancelTokenSource } from 'axios';
import { ApiResponse } from '@/models/common';
import { PendingRequest } from '@/models/services';

class RequestManager {
  private pendingRequests: Map<string, PendingRequest> = new Map();
  private readonly REQUEST_TIMEOUT = 30000; // 30 seconds
  private readonly DUPLICATE_THRESHOLD = 50; // 50ms threshold for true duplicates only
  private readonly MAX_CONCURRENT_SAME_REQUESTS = 3; // Allow up to 3 concurrent requests for same resource

  private generateRequestKey(url: string, method: string, data?: any): string {
    const dataString = data ? JSON.stringify(data) : '';
    return `${method.toUpperCase()}:${url}:${dataString}`;
  }

  private cleanupExpiredRequests(): void {
    const now = Date.now();
    for (const [key, request] of this.pendingRequests.entries()) {
      if (now - request.timestamp > this.REQUEST_TIMEOUT) {
        request.cancelToken.cancel('Request timeout');
        this.pendingRequests.delete(key);
      }
    }
  }

  private isDuplicateRequest(key: string, timestamp: number): boolean {
    const existingRequest = this.pendingRequests.get(key);
    if (!existingRequest) return false;
    
    // Only consider it a duplicate if it's within 50ms (true accidental double-click)
    return timestamp - existingRequest.timestamp < this.DUPLICATE_THRESHOLD;
  }

  private getConcurrentRequestCount(key: string): number {
    let count = 0;
    for (const [requestKey] of this.pendingRequests.entries()) {
      if (requestKey === key) {
        count++;
      }
    }
    return count;
  }

  public createRequest(url: string, method: string, data?: any): { cancelToken: CancelTokenSource; shouldProceed: boolean } {
    this.cleanupExpiredRequests();
    
    const key = this.generateRequestKey(url, method, data);
    const timestamp = Date.now();
    
    // Check for true duplicate (within 50ms - likely accidental double-click)
    if (this.isDuplicateRequest(key, timestamp)) {
      console.warn(`True duplicate request detected and cancelled: ${key}`);
      return { cancelToken: axios.CancelToken.source(), shouldProceed: false };
    }
    
    // Check concurrent request limit
    const concurrentCount = this.getConcurrentRequestCount(key);
    if (concurrentCount >= this.MAX_CONCURRENT_SAME_REQUESTS) {
      console.warn(`Too many concurrent requests for ${key}, allowing but monitoring`);
      // Don't cancel, but allow the request to proceed
    }
    
    // Only cancel existing request if it's very old (more than 5 seconds)
    const existingRequest = this.pendingRequests.get(key);
    if (existingRequest && (timestamp - existingRequest.timestamp) > 5000) {
      console.log(`Cancelling old request for ${key}`);
      existingRequest.cancelToken.cancel('Old request replaced');
    }
    
    // Create new request
    const cancelToken = axios.CancelToken.source();
    this.pendingRequests.set(key, {
      url,
      method,
      cancelToken,
      timestamp
    });
    
    return { cancelToken, shouldProceed: true };
  }

  public completeRequest(url: string, method: string, data?: any): void {
    const key = this.generateRequestKey(url, method, data);
    this.pendingRequests.delete(key);
  }

  // Method to create request without duplicate detection (for retries, etc.)
  public createRequestWithoutDuplicateCheck(url: string, method: string, data?: any): { cancelToken: CancelTokenSource; shouldProceed: boolean } {
    this.cleanupExpiredRequests();
    
    const key = this.generateRequestKey(url, method, data);
    const timestamp = Date.now();
    
    // Create new request without duplicate checking
    const cancelToken = axios.CancelToken.source();
    this.pendingRequests.set(key, {
      url,
      method,
      cancelToken,
      timestamp
    });
    
    return { cancelToken, shouldProceed: true };
  }

  public cancelAllRequests(): void {
    for (const request of this.pendingRequests.values()) {
      request.cancelToken.cancel('All requests cancelled');
    }
    this.pendingRequests.clear();
  }

  public getPendingRequestsCount(): number {
    return this.pendingRequests.size;
  }
}

// Global request manager instance
const requestManager = new RequestManager();

// Base URL behavior summary:
// - In development we use Vite's proxy (configured in `vite.config.ts`) so requests should go to `/api/...` on the same origin.
// - In production set the environment variable VITE_API_BASE_URL (e.g. https://regalbillingbackend.onrender.com) in your Pages/hosting provider.
//   When VITE_API_BASE_URL is set, requests will be sent directly to that origin and we will NOT prepend `/api`.
// This avoids relying on Netlify-style `_redirects` proxy rewrites which Cloudflare Pages does not proxy the same way
// (Netlify's `200` rewrite acts as a proxy and preserves methods; Cloudflare Pages will not behave identically).
const RAW_BASE = (import.meta.env?.VITE_API_BASE_URL as string) ?? '';
// remove trailing slash for consistency
const BASE = RAW_BASE.replace(/\/+$/, '');

// Create axios instance with baseURL when provided. If BASE is empty we keep requests relative which lets the
// dev server proxy (vite) handle `/api` requests.
const api: AxiosInstance = axios.create({
  baseURL: BASE || undefined,
  timeout: 30000, // Increased timeout to 30 seconds
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // Get token from localStorage
    const token = localStorage.getItem('jwt');
    
    // Add authorization header if token exists
    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    // URL handling:
    // - If no BASE is configured (development), keep the existing behaviour of prefixing `/api` so vite proxy works.
    // - If BASE is configured (production), do not prefix and allow axios.baseURL + config.url to form the URL.
    if (config.url) {
      if (!BASE) {
        if (!config.url.startsWith('/api')) {
          config.url = `/api${config.url}`;
        }
      } else {
        // Ensure the url starts with a slash for correct concatenation with baseURL
        if (!/^https?:\/\//i.test(config.url) && !config.url.startsWith('/')) {
          config.url = `/${config.url}`;
        }
      }
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response: AxiosResponse) => {
    return response;
  },
  (error) => {
    // Don't log cancellation errors
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }
    
    // Handle authentication errors
    if (error.response?.status === 401) {
      // Clear token and redirect to login
      localStorage.removeItem('jwt');
      window.location.href = '/login';
    }
    
    // Handle other errors
    if (error.response?.status === 403) {
      console.error('Access forbidden');
    }
    
    if (error.response?.status >= 500) {
      console.error('Server error:', error.response.data);
    }
    
    return Promise.reject(error);
  }
);

// API service class
export class ApiService {
  // Generic GET request with cancellation
  static async get<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const { cancelToken, shouldProceed } = requestManager.createRequest(url, 'GET');
    
    if (!shouldProceed) {
      throw new Error('Duplicate request cancelled');
    }
    
    try {
      const response = await api.get<any>(url, { 
        ...config, 
        cancelToken: cancelToken.token 
      });
      
      requestManager.completeRequest(url, 'GET');
      
      // Handle both ApiResponse format and direct data format
      if (response.data && typeof response.data === 'object') {
        // Check if it's already in ApiResponse format
        if ('success' in response.data && 'data' in response.data) {
          return response.data as ApiResponse<T>;
        } else {
          // Convert direct data to ApiResponse format
          return {
            success: true,
            data: response.data as T,
            message: 'Success'
          };
        }
      }
      
      return {
        success: true,
        data: response.data as T,
        message: 'Success'
      };
    } catch (error: any) {
      requestManager.completeRequest(url, 'GET');
      throw this.handleError(error);
    }
  }

  // Generic POST request with cancellation
  static async post<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const { cancelToken, shouldProceed } = requestManager.createRequest(url, 'POST', data);
    
    if (!shouldProceed) {
      throw new Error('Duplicate request cancelled');
    }
    
    try {
      const response = await api.post<any>(url, data, { 
        ...config, 
        cancelToken: cancelToken.token 
      });
      
      requestManager.completeRequest(url, 'POST', data);
      
      // Handle both ApiResponse format and direct data format
      if (response.data && typeof response.data === 'object') {
        // Check if it's already in ApiResponse format
        if ('success' in response.data && 'data' in response.data) {
          return response.data as ApiResponse<T>;
        } else {
          // Convert direct data to ApiResponse format
          return {
            success: true,
            data: response.data as T,
            message: 'Success'
          };
        }
      }
      
      return {
        success: true,
        data: response.data as T,
        message: 'Success'
      };
    } catch (error: any) {
      requestManager.completeRequest(url, 'POST', data);
      throw this.handleError(error);
    }
  }

  // Generic PUT request with cancellation
  static async put<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const { cancelToken, shouldProceed } = requestManager.createRequest(url, 'PUT', data);
    
    if (!shouldProceed) {
      throw new Error('Duplicate request cancelled');
    }
    
    try {
      const response = await api.put<any>(url, data, { 
        ...config, 
        cancelToken: cancelToken.token 
      });
      
      requestManager.completeRequest(url, 'PUT', data);
      
      // Handle both ApiResponse format and direct data format
      if (response.data && typeof response.data === 'object') {
        // Check if it's already in ApiResponse format
        if ('success' in response.data && 'data' in response.data) {
          return response.data as ApiResponse<T>;
        } else {
          // Convert direct data to ApiResponse format
          return {
            success: true,
            data: response.data as T,
            message: 'Success'
          };
        }
      }
      
      return {
        success: true,
        data: response.data as T,
        message: 'Success'
      };
    } catch (error: any) {
      requestManager.completeRequest(url, 'PUT', data);
      console.error('API PUT error:', error);
      throw this.handleError(error);
    }
  }

  // Generic DELETE request with cancellation
  static async delete<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const { cancelToken, shouldProceed } = requestManager.createRequest(url, 'DELETE');
    
    if (!shouldProceed) {
      throw new Error('Duplicate request cancelled');
    }
    
    try {
      const response = await api.delete<any>(url, { 
        ...config, 
        cancelToken: cancelToken.token 
      });
      
      requestManager.completeRequest(url, 'DELETE');
      
      // Handle both ApiResponse format and direct data format
      if (response.data && typeof response.data === 'object') {
        // Check if it's already in ApiResponse format
        if ('success' in response.data && 'data' in response.data) {
          return response.data as ApiResponse<T>;
        } else {
          // Convert direct data to ApiResponse format
          return {
            success: true,
            data: response.data as T,
            message: 'Success'
          };
        }
      }
      
      return {
        success: true,
        data: response.data as T,
        message: 'Success'
      };
    } catch (error: any) {
      requestManager.completeRequest(url, 'DELETE');
      throw this.handleError(error);
    }
  }

  // Generic PATCH request with cancellation
  static async patch<T>(url: string, data?: any, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const { cancelToken, shouldProceed } = requestManager.createRequest(url, 'PATCH', data);
    
    if (!shouldProceed) {
      throw new Error('Duplicate request cancelled');
    }
    
    try {
      const response = await api.patch<any>(url, data, { 
        ...config, 
        cancelToken: cancelToken.token 
      });
      
      requestManager.completeRequest(url, 'PATCH', data);
      
      // Handle both ApiResponse format and direct data format
      if (response.data && typeof response.data === 'object') {
        // Check if it's already in ApiResponse format
        if ('success' in response.data && 'data' in response.data) {
          return response.data as ApiResponse<T>;
        } else {
          // Convert direct data to ApiResponse format
          return {
            success: true,
            data: response.data as T,
            message: 'Success'
          };
        }
      }
      
      return {
        success: true,
        data: response.data as T,
        message: 'Success'
      };
    } catch (error: any) {
      requestManager.completeRequest(url, 'PATCH', data);
      throw this.handleError(error);
    }
  }

  // Error handler
  private static handleError(error: any): Error {
    // Don't throw for cancelled requests
    if (axios.isCancel(error)) {
      return new Error('Request cancelled');
    }
    
    if (error.response?.data?.message) {
      return new Error(error.response.data.message);
    }
    
    if (error.message) {
      return new Error(error.message);
    }
    
    return new Error('An unexpected error occurred');
  }

  // Utility methods for request management
  static cancelAllRequests(): void {
    requestManager.cancelAllRequests();
  }

  static getPendingRequestsCount(): number {
    return requestManager.getPendingRequestsCount();
  }

  // GET request without duplicate detection (for retries, etc.)
  static async getWithoutDuplicateCheck<T>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const { cancelToken } = requestManager.createRequestWithoutDuplicateCheck(url, 'GET');
    
    try {
      const response = await api.get<any>(url, { 
        ...config, 
        cancelToken: cancelToken.token 
      });
      
      requestManager.completeRequest(url, 'GET');
      
      // Handle both ApiResponse format and direct data format
      if (response.data && typeof response.data === 'object') {
        // Check if it's already in ApiResponse format
        if ('success' in response.data && 'data' in response.data) {
          return response.data as ApiResponse<T>;
        } else {
          // Convert direct data to ApiResponse format
          return {
            success: true,
            data: response.data as T,
            message: 'Success'
          };
        }
      }
      
      return {
        success: true,
        data: response.data as T,
        message: 'Success'
      };
    } catch (error: any) {
      requestManager.completeRequest(url, 'GET');
      throw this.handleError(error);
    }
  }
}

// Export the axios instance for direct use if needed
export default api; 